import { ValueGetterFunc } from "@ag-grid-community/core";
import clsx from "clsx";
import {
  disableDataTableField,
  getUpdatedInterestRateWhenOutstandingChanges,
  getUpdatedOutstandingAmountWhenRateChanges,
  RefreshCellsOnInputChange,
} from "features/credit-facility/expenses/adjustments-table/utils";
import {
  useCreditFacilityInterestExpenseAdjustmentsTableRetrieveSuspense,
  useCreditFacilityLoanRetrieveSuspense,
} from "gen/endpoints/credit-facility/credit-facility";
import { CreditFacilityExpenseAdjustmentsUpdateDataRequest } from "gen/model/creditFacilityExpenseAdjustmentsUpdateDataRequest";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import { MoneyTableCellValue } from "gen/model/moneyTableCellValue";
import { PercentTableCellValue } from "gen/model/percentTableCellValue";
import get from "lodash-es/get";
import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { DataTableHeaderOrCellClass } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { decimalOrZero } from "shared/utils/DecimalUtils";

const headerOrCellClass: DataTableHeaderOrCellClass = (props) =>
  clsx({
    "or-column": props.column?.getColId() == "or",
  });

export const InterestExpenseAdjustmentsTable: React.FC<{
  loanId: string;
  form: UseFormReturn<CreditFacilityExpenseAdjustmentsUpdateDataRequest>;
}> = ({ loanId, form }) => {
  const { t } = useTranslation(["creditFacility", "enums"]);
  const { fundFamilyId, creditFacilityId, expenseEventId } =
    useRequiredStringParams();
  const { data: adjustmentsData } =
    useCreditFacilityInterestExpenseAdjustmentsTableRetrieveSuspense(
      fundFamilyId,
      creditFacilityId,
      expenseEventId,
      {},
      {
        query: {
          refetchOnMount: "always",
        },
      },
    );

  const { data: loan } = useCreditFacilityLoanRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    loanId,
  );

  /* eslint-disable @typescript-eslint/no-unnecessary-condition */
  const valueGetter = useCallback<
    ValueGetterFunc<GenericTableRow, GenericTableCell>
  >(
    (params) => {
      const formValues = form.getValues();
      const colId = params.column.getColId();
      const columnData = params.node?.data;
      if (!columnData) {
        return;
      }
      switch (colId) {
        case "updated_interest_expense_amount": {
          const updatedExpenseAmount =
            getUpdatedOutstandingAmountWhenRateChanges(
              columnData,
              formValues,
              loan.rate_day_count,
            );
          return {
            value: {
              type: "money",
              amount: updatedExpenseAmount.toString(),
            } as MoneyTableCellValue,
          } as GenericTableCell;
        }
        case "updated_interest_rate": {
          const updatedInterestRate =
            getUpdatedInterestRateWhenOutstandingChanges(
              columnData,
              formValues,
              loan.rate_day_count,
            );
          return {
            value: {
              type: "percent",
              value: updatedInterestRate?.toString() ?? "",
            } as PercentTableCellValue,
          } as GenericTableCell;
        }
        case "interest_rate_adjustment": {
          const adjustedOutstanding = decimalOrZero(
            get(
              formValues,
              columnData.cells["interest_expense_amount_adjustment"]?.form_field
                ?.name as string,
            ),
          );
          if (!adjustedOutstanding.isZero()) {
            return disableDataTableField({
              columnData,
              colId,
            });
          }
          break;
        }
        case "interest_expense_amount_adjustment": {
          const adjustedRate = decimalOrZero(
            get(
              formValues,
              columnData.cells["interest_rate_adjustment"]?.form_field
                ?.name as string,
            ),
          );
          if (!adjustedRate.isZero()) {
            return disableDataTableField({
              columnData,
              colId,
            });
          }
          break;
        }

        default:
          break;
      }
    },
    [form, loan.rate_day_count],
  );

  return (
    <TableBox isFullHeight={true} isFullWidth={true}>
      <TableBoxHeader>
        <TableBoxTitle>
          {`${formatIsoDateToDisplay(
            adjustmentsData.period_start,
          )} - ${formatIsoDateToDisplay(
            adjustmentsData.period_end,
          )}: ${t("creditFacility:postExpense.dailyInterestExpense")}`}
        </TableBoxTitle>
        <DataTableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        <RefreshCellsOnInputChange form={form}>
          <DataTable
            sx={{
              ".or-column > *": {
                justifyContent: "center",
              },
            }}
            headerOrCellClass={headerOrCellClass}
            data={adjustmentsData.table}
            shouldUnregisterFormFields={false}
            valueGetter={valueGetter}
          />
        </RefreshCellsOnInputChange>
      </TableBoxBody>
    </TableBox>
  );
};
